import { useState, useEffect, useRef } from "react";
import {
  Bot,
  X,
  Send,
  Sparkles,
  Maximize2,
  Minimize2,
  Loader2,
} from "lucide-react";
import { COPILOT_MODES, COPILOT_SUGGESTIONS } from "../../lib/sample-data";
import { aiApi } from "../../lib/api";
import { cn } from "../../lib/utils";

const SESSION_KEY = "premierlab.copilot.session";

function getSessionId() {
  if (typeof window === "undefined") return "session";
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `s-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

export default function PremierCopilot({ openExternal, onCloseExternal }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openExternal ?? internalOpen;
  const setOpen = (v) => {
    if (openExternal !== undefined) {
      if (!v) onCloseExternal?.();
    } else {
      setInternalOpen(v);
    }
  };

  const [docked, setDocked] = useState(false);
  const [mode, setMode] = useState("strategy");
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "I'm Premier Copilot. Ask me to plan a launch, write a chapter, design a deck, or build an automation.",
    },
  ]);
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || loading) return;
    setMessages((m) => [...m, { role: "user", content: trimmed }]);
    setInput("");
    setLoading(true);
    try {
      const res = await aiApi.chat({
        session_id: sessionId.current,
        message: trimmed,
        mode,
      });
      setMessages((m) => [
        ...m,
        { role: "assistant", content: res.reply, meta: `${res.provider} · ${res.model}` },
      ]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "I hit a snag reaching the model. Check the AI key in AdminLab → API Keys and try again.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          data-testid="copilot-bubble"
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-5 lg:bottom-6 lg:right-24 z-[100] h-14 w-14 rounded-full grid place-items-center text-[#061414] pl-pulse"
          style={{
            background:
              "linear-gradient(135deg, #00B8A9 0%, #27E6D1 60%, #B9FF2F 100%)",
            boxShadow: "0 0 32px rgba(39, 230, 209, 0.55)",
          }}
          aria-label="Open Premier Copilot"
        >
          <Bot className="h-6 w-6" />
        </button>
      )}

      {/* Panel / Docked */}
      {open && (
        <div
          data-testid="copilot-panel"
          className={cn(
            "fixed z-[100] flex flex-col rounded-2xl pl-glass overflow-hidden",
            docked
              ? "right-0 top-0 bottom-0 w-full sm:w-[400px] rounded-none border-l border-[var(--pl-border)]"
              : "bottom-24 right-4 lg:bottom-6 lg:right-24 w-[calc(100vw-2rem)] sm:w-[420px] h-[640px] max-h-[80vh]"
          )}
          style={{
            background: "rgba(10, 28, 28, 0.92)",
            border: "1px solid var(--pl-border)",
            boxShadow: "0 24px 80px -20px rgba(0,0,0,0.6)",
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--pl-border)]">
            <div className="h-9 w-9 rounded-xl pl-signature-gradient grid place-items-center">
              <Sparkles className="h-4 w-4 text-[#061414]" strokeWidth={2.4} />
            </div>
            <div className="flex-1 leading-tight">
              <div className="text-[13px] font-semibold text-[var(--pl-text)] font-display">
                Premier Copilot
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-text-muted)]">
                {mode} mode
              </div>
            </div>
            <button
              data-testid="copilot-dock-btn"
              onClick={() => setDocked((d) => !d)}
              className="h-8 w-8 rounded-lg grid place-items-center text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)]"
              aria-label="Dock"
            >
              {docked ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <button
              data-testid="copilot-close-btn"
              onClick={() => setOpen(false)}
              className="h-8 w-8 rounded-lg grid place-items-center text-[var(--pl-text-muted)] hover:text-rose-400"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Mode chips */}
          <div className="px-3 pt-3 pb-2 flex gap-1.5 overflow-x-auto pl-noscroll">
            {COPILOT_MODES.map((m) => (
              <button
                key={m.id}
                data-testid={`copilot-mode-${m.id}`}
                onClick={() => setMode(m.id)}
                className={cn(
                  "shrink-0 px-3 py-1 rounded-full text-[11px] tracking-wide border transition",
                  mode === m.id
                    ? "bg-[var(--pl-aqua)]/15 text-[var(--pl-aqua)] border-[var(--pl-aqua)]/40"
                    : "text-[var(--pl-text-muted)] border-[var(--pl-border)] hover:text-[var(--pl-text)]"
                )}
              >
                {m.label}
              </button>
            ))}
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`copilot-msg-${i}`}
                className={cn(
                  "rounded-xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap",
                  m.role === "user"
                    ? "ml-auto max-w-[85%] bg-[var(--pl-aqua)]/15 text-[var(--pl-text)] border border-[var(--pl-aqua)]/30"
                    : "mr-auto max-w-[92%] bg-white/[0.04] text-[var(--pl-text)] border border-[var(--pl-border)]"
                )}
              >
                {m.content}
                {m.meta && (
                  <div className="mt-1 text-[10px] font-mono text-[var(--pl-text-muted)]">
                    {m.meta}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="mr-auto px-3.5 py-2.5 rounded-xl bg-white/[0.04] border border-[var(--pl-border)] text-sm text-[var(--pl-text-muted)] flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Thinking…
              </div>
            )}
          </div>

          {/* Suggestions */}
          {messages.length <= 1 && !loading && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5">
              {COPILOT_SUGGESTIONS.slice(0, 4).map((s) => (
                <button
                  key={s}
                  data-testid={`copilot-suggest-${s.slice(0, 12)}`}
                  onClick={() => send(s)}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-[var(--pl-border)] text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)] hover:border-[var(--pl-aqua)]/40 transition"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-[var(--pl-border)] flex items-end gap-2">
            <textarea
              data-testid="copilot-input"
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Ask Premier Copilot…"
              className="flex-1 resize-none max-h-32 bg-white/[0.04] border border-[var(--pl-border)] rounded-xl px-3 py-2.5 text-sm text-[var(--pl-text)] placeholder:text-[var(--pl-text-muted)] focus:outline-none focus:border-[var(--pl-aqua)]/40"
            />
            <button
              data-testid="copilot-send-btn"
              onClick={() => send()}
              disabled={loading || !input.trim()}
              className="h-10 w-10 rounded-xl pl-btn-aqua grid place-items-center disabled:opacity-50"
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
